// src/components/DashboardReply.js
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Paper, Tabs, Tab,
    Snackbar, Alert, Stack, Pagination
} from '@mui/material';
import excuseApi from '../../api/excuseApi';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Layout/SideBar';

const getAvatarUrl = (obj) => {
    if (!obj) return `https://ui-avatars.com/api/?name=Anon&background=random`;
  
    if (obj.avatar) {
      return `${process.env.REACT_APP_STATIC_URL}${obj.avatar}`;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(obj.username || 'Anon')}&background=random`;
  };

const DashboardReply = () => {
    const [tabValue, setTabValue] = useState('comments');
    const [comments, setComments] = useState([]);
    const [feelings, setFeelings] = useState([]);
    const [pageComments, setPageComments] = useState(1);
    const [pageFeelings, setPageFeelings] = useState(1);
    const [totalComments, setTotalComments] = useState(0);
    const [totalFeelings, setTotalFeelings] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const navigate = useNavigate();

    const perPage = 10;

    const checkHasSubcomments = async (parentId) => {
        try {
            const res = await excuseApi({
                endpoint: `/comments/${parentId}/subcomments`,
                method: 'GET',
            });
            return (res && res.length > 0);
        } catch {
            return false;
        }
    };

    const loadComments = async (pageNum = 1) => {
        try {
            const res = await excuseApi({
                endpoint: `/comments-multi-target?target_type=article&target_type=feeling&page=${pageNum}&per_page=${perPage}`,
                method: 'GET',
            });

            const items = res.items || [];
            const updated = await Promise.all(
                items.map(async c => {
                    const hasSubs = await checkHasSubcomments(c.id);
                    return { ...c, hasSubcomments: hasSubs };
                })
            );

            setComments(updated);
            setTotalComments(res.total || 0);
        } catch (err) {
            throw err;
        }
    };

    const loadFeelings = async (pageNum = 1) => {
        try {
            const res = await excuseApi({
                endpoint: `/feelings?page=${pageNum}&per_page=${perPage}`,
                method: 'GET',
            });

            const items = res.items || [];
            const updated = await Promise.all(
                items.map(async f => {
                    const hasSubs = await checkHasSubcomments(f.id);
                    return { ...f, hasSubcomments: hasSubs };
                })
            );

            setFeelings(updated);
            setTotalFeelings(res.total || 0);
        } catch (err) {
            throw err;
        }
    };

    const loadSubcomments = async (parentId, type) => {
        try {
            const res = await excuseApi({
                endpoint: `/comments/${parentId}/subcomments`,
                method: 'GET',
            });
            if (type === 'comment') {
                setComments(prev =>
                    prev.map(c => c.id === parentId ? { ...c, subcomments: res || [] } : c)
                );
            } else {
                setFeelings(prev =>
                    prev.map(f => f.id === parentId ? { ...f, subcomments: res || [] } : f)
                );
            }
        } catch (err) {
            throw err;
        }
    };

    const deleteCommentCascade = async (id, type = 'comment') => {
        if (!window.confirm('Bạn có chắc muốn xóa comment này và toàn bộ comment con?')) return;
        try {
            await excuseApi({
                endpoint: `/comments/${id}?cascade=true`,
                method: 'DELETE',
            });
            setSnackbar({ open: true, message: 'Xóa comment thành công', severity: 'success' });
            if (type === 'comment') {
                loadComments(pageComments);
            } else {
                loadFeelings(pageFeelings);
            }
        } catch (err) {
            setSnackbar({ open: true, message: 'Xóa comment thất bại', severity: 'error' });
        }
    };

    const deleteFeelingCascade = async (id) => {
        if (!window.confirm('Bạn có chắc muốn xóa cảm nhận này và toàn bộ comment liên quan?')) return;
        try {
            await excuseApi({
                endpoint: `/feelings/${id}?cascade=true`,
                method: 'DELETE',
            });
            setSnackbar({ open: true, message: 'Xóa cảm nhận thành công', severity: 'success' });
            loadFeelings(pageFeelings);
        } catch (err) {
            setSnackbar({ open: true, message: 'Xóa cảm nhận thất bại', severity: 'error' });
        }
    };

    useEffect(() => {
        if (tabValue === 'comments') {
            loadComments(pageComments);
        } else {
            loadFeelings(pageFeelings);
        }
    }, [tabValue, pageComments, pageFeelings]);

    const renderSubcomments = (subs, type) => (
        subs?.map((sub) => (
            <Paper
                key={sub.id}
                sx={{
                    p: 1.5, ml: 6, mt: 1,
                    bgcolor: '#f9f9f9',
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
            >
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <img src={getAvatarUrl(sub)} alt={sub.username || 'Ẩn danh'} width={28} height={28} style={{ borderRadius: '50%' }} />
                    <Typography fontWeight="bold">{sub.username || 'Ẩn danh'}</Typography>
                    <Typography variant="caption" sx={{ ml: 1 }}>
                        {new Intl.DateTimeFormat('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }).format(new Date(sub.created_at))}
                    </Typography>
                </Box>
                <Box sx={{ mt: 0.5 }} dangerouslySetInnerHTML={{ __html: sub.content }} />
                <Button
                    size="small"
                    color="error"
                    sx={{ mt: 0.5 }}
                    onClick={() => deleteCommentCascade(sub.id, type)}
                >
                    Xóa
                </Button>
            </Paper>
        ))
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Sidebar */}
            <Box sx={{ flexBasis: 240, flexShrink: 0 }}>
                <Sidebar />
            </Box>

            {/* Main content */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', p: 3 }}>
                <Box sx={{ width: '100%', maxWidth: 900 }}>
                    <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
                        <Tab label="Bình luận" value="comments" />
                        <Tab label="Cảm nhận" value="feelings" />
                    </Tabs>

                    {/* Comments */}
                    {tabValue === 'comments' && comments.map((c) => (
                        <Paper key={c.id} sx={{ p: 2, mb: 2, borderLeft: '5px solid #8a0000', borderRadius: 2, boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <img src={getAvatarUrl(c)} alt={c.username || 'Ẩn danh'} width={40} height={40} style={{ borderRadius: '50%' }} />
                                <Box>
                                    <Typography fontWeight="bold">{c.username || 'Ẩn danh'}</Typography>
                                    <Typography variant="caption">
                                        {new Intl.DateTimeFormat('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }).format(new Date(c.created_at))}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: c.content }} />
                            {c.article_id && (
                                <Typography
                                    variant="body2"
                                    color="primary"
                                    sx={{ mt: 1, cursor: 'pointer' }}
                                    onClick={() => navigate(`/articles/${c.article_id}`)}
                                >
                                    Bình luận từ bài viết: {c.article_title}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Button size="small" color="error" onClick={() => deleteCommentCascade(c.id, 'comment')}>Xóa</Button>
                                <Button
                                    size="small"
                                    onClick={() => loadSubcomments(c.id, 'comment')}
                                    disabled={!c.hasSubcomments}
                                    sx={{
                                        color: !c.hasSubcomments ? 'grey.500' : 'primary.main'
                                    }}
                                >
                                    Xem phản hồi
                                </Button>
                            </Stack>
                            {renderSubcomments(c.subcomments, 'comment')}
                        </Paper>
                    ))}

                    {tabValue === 'comments' && totalComments > perPage && (
                        <Box display="flex" justifyContent="center" my={2}>
                            <Pagination
                                count={Math.ceil(totalComments / perPage)}
                                page={pageComments}
                                onChange={(e, value) => setPageComments(value)}
                            />
                        </Box>
                    )}

                    {/* Feelings */}
                    {tabValue === 'feelings' && feelings.map((f) => (
                        <Paper key={f.id} sx={{ p: 2, mb: 2, borderLeft: '5px solid #8a0000', borderRadius: 2, boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <img src={getAvatarUrl(f)} alt={f.username || 'Ẩn danh'} width={40} height={40} style={{ borderRadius: '50%' }} />
                                <Box>
                                    <Typography fontWeight="bold">{f.username || 'Ẩn danh'}</Typography>
                                    <Typography variant="caption">
                                        {new Intl.DateTimeFormat('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' }).format(new Date(f.created_at))}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: f.content }} />
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Button size="small" color="error" onClick={() => deleteFeelingCascade(f.id)}>Xóa</Button>
                                <Button
                                    size="small"
                                    onClick={() => loadSubcomments(f.id, 'feeling')}
                                    disabled={!f.hasSubcomments}
                                    sx={{
                                        color: !f.hasSubcomments ? 'grey.500' : 'primary.main'
                                    }}
                                >
                                    Xem bình luận
                                </Button>
                            </Stack>
                            {renderSubcomments(f.subcomments, 'feeling')}
                        </Paper>
                    ))}

                    {tabValue === 'feelings' && totalFeelings > perPage && (
                        <Box display="flex" justifyContent="center" my={2}>
                            <Pagination
                                count={Math.ceil(totalFeelings / perPage)}
                                page={pageFeelings}
                                onChange={(e, value) => setPageFeelings(value)}
                            />
                        </Box>
                    )}

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        sx={{
                            '& .MuiPaper-root': {
                                minWidth: '300px',
                                padding: '12px 20px',
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                textAlign: 'center',
                            }
                        }}
                    >
                        <Alert
                            severity={snackbar.severity}
                            sx={{
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                justifyContent: 'center',
                                textAlign: 'center',
                                width: '100%'
                            }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Box>
            </Box>
        </Box>
    );
};

export default DashboardReply;
